import { Card, Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Rest from "../../../../libraries/Rest";
import Alert from "../../../../libraries/Alert";
import * as Components from "../../../../components/Components";
import Validator from "../../../../libraries/Validator";

function CompanyPositionForm() {
    const { companyId, id } = useParams();
    const navigate = useNavigate();
    const rest = new Rest();

    const isCreate = id?.toLowerCase() === 'add' ? true : false;

    /**
     * Init validator of payload
     */
    const validator = new Validator({
        department_id: { required: true },
        code: { required: true },
        name: { required: true }
    });

    /**
     * Init states
     */
    const rootPath = `/companies/positions/${companyId}`;
    const endpoint = `/companies/positions/${companyId}`;

    let [payload, setPayload] = useState({
        parent_id: null,
        department_id: null,
        name: null,
        description: null,
    });
    let [positions, setPositions] = useState([]);
    let [departments, setDepartments] = useState([]);

    /**
     * First init function
     */
    const getDataById = async () => {
        const response = await rest.get(`${endpoint}/${id}`);
        if (response?.data?.data !== undefined) {
            const data = response.data.data;
            setPayload({
                parent_id: data?.parent_id,
                department_id: data?.department_id,
                code: data?.code,
                name: data?.name,
                description: data?.description,
            });

            /**
             * Load positions to make default options
             */
            if (data?.department_id !== undefined && data.department_id) await getPositions(data.department_id);
        } else {
            Alert.showMessage('Error', 'error', 'Data is not found');
            navigate(rootPath);
        }
    }

    const getPositions = async (department_id = null) => {
        if (!department_id) {
            setPositions([]); // reset positions as no options
            setPayload({ ...payload, parent_id: null }); // reset parent_id to prevent data not valid
            return;
        }

        Alert.showLoading();
        const response = await rest.get(`${endpoint}?page=1&limit=100&department_id=${department_id}`);
        if (response?.data?.data?.data !== undefined) {
            const data = response?.data?.data?.data;

            /**
             * Translate data to option format
             */
            let options = [];
            for (let i = 0; i < data.length; i++) {
                /**
                 * Prevent to load same positions in the options
                 */
                if (data[i]['id'] === id) continue;

                /**
                 * Push data to options
                 */
                options.push({
                    value: data[i]['id'] !== undefined ? data[i]['id'] : null,
                    label: data[i]['name'] !== undefined ? data[i]['name'] : null
                });
            }
            setPositions(options);
        }
        Alert.close();
    }

    const getDepartments = async () => {
        const response = await rest.get(`/companies/departments/${companyId}?page=1&limit=100`);
        if (response?.data?.data?.data !== undefined) {
            const data = response?.data?.data?.data;

            /**
             * Translate data to option format
             */
            let options = [];
            for (let i = 0; i < data.length; i++) {
                options.push({
                    value: data[i]['id'] !== undefined ? data[i]['id'] : null,
                    label: data[i]['name'] !== undefined ? data[i]['name'] : null
                });
            }
            setDepartments(options);
        }
    }

    useEffect(() => {
        async function __init() {
            Alert.showLoading();
            await getDepartments();
            if (!isCreate) await getDataById();
            Alert.close();
        }
        __init();
    }, []);


    /**
     * Action handlers
     */
    const updatePayload = async (field, value) => {
        setPayload({ ...payload, [field]: value });

        /**
         * Call positions when department has changed
         * So positions will load with department_id filter
         */
        if (field === 'department_id') await getPositions(value);
    }

    const submit = async () => {
        /**
         * Validate input
         */
        const errMessages = validator.validate(payload);
        if (errMessages.length > 0) {
            let errMessageStrings = ``;
            for (let i = 0; i < errMessages.length; i++) {
                errMessageStrings += `${errMessages[i]}<br />`;
            }
            return Alert.showMessage(`Invalid input`, `warning`, errMessageStrings);
        }

        /**
         * Submit action
         */
        Alert.showConfirm({
            url: endpoint + (!isCreate ? `/${id}` : ``),
            method: isCreate ? 'POST' : 'PUT',
            data: payload,
        }).then((result) => {
            if (result.isConfirmed) {
                if (result?.value?.success) {
                    Alert.showMessage('Success', 'success', result?.value?.data?.message);
                    navigate(rootPath);
                } else {
                    Alert.showMessage('Failure', 'error', result?.value?.message);
                }
            }
        });
    }

    /**
     * Rendering DOM
     */
    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <ol className="breadcrumb fs-sm mb-1">
                        <li className="breadcrumb-item">
                            <Link to="/dashboard">Dashboard</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/companies">List companies</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to={rootPath}>List positions</Link>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Form {isCreate ? 'create' : 'edit'}
                        </li>
                    </ol>
                </div>
            </div>

            <Row className="g-3">
                <Col xl="12">
                    <Card className="card">
                        <Card.Header className="bg-primary">
                            <Card.Title
                                as="label"
                                className="fs-sm fw-medium mb-1 text-white"
                            >
                                Form {isCreate ? 'create' : 'edit'} positions
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="bg-white">
                            <Link to={rootPath} className="btn btn-sm btn-warning text-white">
                                <i className="ri-arrow-left-line"></i> Back
                            </Link>
                            &nbsp;
                            <button className="btn btn-sm btn-primary" onClick={submit}>
                                <i className="ri-check-line"></i> Save
                            </button>
                            <br /><br />
                            <Row className="g-3">
                                <Col sm="6">
                                    <Components.Select
                                        name="department_id"
                                        label="Department"
                                        defaultValue={payload.department_id}
                                        options={departments}
                                        change={updatePayload}
                                    />
                                </Col>
                                <Col sm="6">
                                    <Components.Select
                                        name="parent_id"
                                        label="Parent ID"
                                        defaultValue={payload.parent_id}
                                        options={positions}
                                        change={updatePayload}
                                    />
                                </Col>
                                <Col sm="4">
                                    <Components.Input
                                        type="text"
                                        name="code"
                                        label="Position code"
                                        placeholder="Position code"
                                        defaultValue={payload.code}
                                        change={updatePayload}
                                    />
                                </Col>
                                <Col sm="4">
                                    <Components.Input
                                        type="text"
                                        name="name"
                                        label="Position name"
                                        placeholder="Position name"
                                        defaultValue={payload.name}
                                        change={updatePayload}
                                    />
                                </Col>
                                <Col sm="4">
                                    <Components.Input
                                        type="text"
                                        name="description"
                                        label="Description"
                                        placeholder="Description"
                                        defaultValue={payload.description}
                                        change={updatePayload}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default CompanyPositionForm;